import React from 'react';
import PropTypes from 'prop-types';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { t } from '../../services/i18nService';
import utilService from '../../services/UtilService';

import styles from './QuestionDisplay.scss';
import sessionReader from '../../store/readHelpers/SessionReader';
import localCacheService from '../../services/LocalCache';

const MINIMUM_SKETCH_HEIGHT = 100; // We do not know the actual image height. Using this low number to verify that something has been rendered.
const DEFAULT_NUMBER_OF_RETRIES = 200;
const DEFAULT_WAIT_BETWEEN_RETRIES = 100; // milliseconds

let setCropperData;

class SketchItem extends React.PureComponent {
    static propTypes = {
        includeLink: PropTypes.bool.isRequired,
        index: PropTypes.number.isRequired,
        totalNumberOfSketches: PropTypes.number.isRequired,
        sketchContentItemId: PropTypes.string.isRequired,
        onReady: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);
        this.localCache = localCacheService.getLocalCacheInstance();
        this.state = {
            imageLoaded: false,
            canvasRatio: 1,
            canvasHeight: 400,
            overlayEnabled: true,
        };
    }

    componentWillUnmount() {
        clearTimeout(this.timeoutHandle);
    }

    storeCropperData = () => {
        const { sketchContentItemId } = this.props;
        if (!this.cropper) {
            return;
        }
        const data = this.cropper.getData();
        const canvasData = this.cropper.getCanvasData();
        const sketchData = {
            data,
            canvasData,
        };
        this.localCache.set(`sketch-${sketchContentItemId}`, JSON.stringify(sketchData));
    };

    isHighEnough = (element, expectedHeight = MINIMUM_SKETCH_HEIGHT) => {
        return element.clientHeight >= expectedHeight;
    };

    reportWhenReady = (element, remainingRetries = DEFAULT_NUMBER_OF_RETRIES, waitMs = DEFAULT_WAIT_BETWEEN_RETRIES) => {
        if (this.isHighEnough(element)) {
            this.reportReadyAfterNextAnimationFrame();
            return;
        }

        if (remainingRetries === 0) {
            this.props.onReady(false);
            return;
        }

        this.timeoutHandle = setTimeout(() => {
            if (this.isHighEnough(element)) {
                return this.props.onReady(true);
            }

            return this.reportWhenReady(element, remainingRetries - 1, waitMs);
        }, waitMs);
    };

    reportReadyAfterNextAnimationFrame = () => {
        utilService.callAfterAnimationFrames(() => {
            this.props.onReady(true);
        });
    };

    onError = () => {
        this.props.onReady(false);
    };

    onLoad = () => {
        this.reportWhenReady(this.imageElement);
        const canvasRatio = this.imageElement.naturalWidth / this.imageElement.naturalHeight;
        const imageWidth = this.imageElement.clientWidth;
        const canvasHeight = Math.round(imageWidth / canvasRatio);
        this.setState({
            imageLoaded: true,
            canvasHeight,
            canvasRatio,
        });
    };

    cropperReady = () => {
        const { sketchContentItemId } = this.props;
        const sketchData = this.localCache.get(`sketch-${sketchContentItemId}`);
        if (this.cropper) {
            if (sketchData) {
                const parsed = JSON.parse(sketchData);
                this.cropper.setData(parsed.data);
                this.cropper.setCanvasData(parsed.canvasData);
            }
        }
    };

    rotateLeft = () => {
        this.rotateImage(-90);
    };

    rotateRight = () => {
        this.rotateImage(90);
    };

    rotateImage = (deg) => {
        this.cropper.rotate(deg);
        this.handleCropperEvent();
    };

    handleCropperEvent = () => {
        clearInterval(setCropperData);
        setCropperData = setTimeout(() => {
            this.storeCropperData();
        }, 500);
    };

    reset = () => {
        const { sketchContentItemId } = this.props;
        this.cropper.reset();
        this.localCache.remove(`sketch-${sketchContentItemId}`);
    };

    toggleOverlay = () => {
        const { overlayEnabled } = this.state;
        this.setState({
            overlayEnabled: !overlayEnabled,
        });
    };

    render() {
        const { includeLink, index, totalNumberOfSketches, sketchContentItemId } = this.props;
        const { imageLoaded, canvasHeight, canvasRatio, overlayEnabled } = this.state;
        const isPrintView = sessionReader.isViewMediaPrint() || sessionReader.isViewMediaPdf();
        return (
            <div className={styles.sketchWrapper}>
                <div className={styles.indexInfo}>
                    {t('question.sketch', { index, totalNumberOfSketches })}
                </div>
                <div className={styles.actions}>
                    {
                        !isPrintView ?
                            <span>
                                <button className="btn" type="button" onClick={this.rotateLeft} aria-label={t('question.sketchRotateLeft')}>
                                    <i className="fa fa-rotate-left" aria-hidden="true" />
                                </button>
                                <button className="btn" type="button" onClick={this.rotateRight} aria-label={t('question.sketchRotateRight')}>
                                    <i className="fa fa-rotate-right" aria-hidden="true" />
                                </button>
                                <button className="btn" type="button" onClick={this.reset} aria-label={t('question.sketchReset')}>
                                    <i className="fa fa-compress" aria-hidden="true" />
                                </button>
                            </span>
                            : null
                    }
                    {
                        includeLink ?
                            <a
                                href={`/file/ci/${sketchContentItemId}/sketch`}
                                target="_blank"
                                aria-label={t('general.opensInNewWindow')}
                                rel="noopener noreferrer"
                                className="btn"
                                title={t('question.sketchOpenImage')}
                            >
                                <i className="fa fa-external-link" aria-hidden="true" />
                                <span className={styles.hiddenImage}>
                                    {t('question.sketchOpenImage')}
                                </span>
                            </a>
                            : null
                    }
                </div>
                {
                    isPrintView ?
                        <img
                            alt=""
                            onError={this.onError}
                            onLoad={this.onLoad}
                            ref={(element) => { this.imageElement = element; }}
                            src={`/file/ci/${sketchContentItemId}/sketch`}
                        />
                        :
                        <div className={styles.sketchOverlayParent}>
                            <button
                                className={`${styles.sketchOverlay} ${overlayEnabled ? 'overlayEnabled' : 'btn'}`}
                                onClick={this.toggleOverlay}
                            >
                                <div className={styles.overlayText}>
                                    <i className="fa fa-search-plus" aria-hidden="true" />
                                    {overlayEnabled ? t('question.sketchEnableZoom') : t('question.sketchDisableZoom')}
                                </div>
                                <div className={styles.overlayBackground} />
                            </button>
                            <img
                                alt=""
                                onError={this.onError}
                                onLoad={this.onLoad}
                                ref={(element) => { this.imageElement = element; }}
                                src={`/file/ci/${sketchContentItemId}/sketch`}
                                className={styles.hiddenImage}
                            />
                            {
                                imageLoaded ?
                                    <Cropper
                                        ref={(cropper) => { this.cropper = cropper; }}
                                        src={`/file/ci/${sketchContentItemId}/sketch`}
                                        style={{
                                            height: canvasHeight,
                                            width: '100%',
                                        }}
                                        // Cropper.js options
                                        aspectRatio={canvasRatio}
                                        toggleDragModeOnDblclick={false}
                                        guides={false}
                                        dragMode="move"
                                        checkCrossOrigin={false}
                                        autoCrop={false}
                                        ready={this.cropperReady}
                                        cropend={this.handleCropperEvent}
                                        zoom={this.handleCropperEvent}
                                    />
                                    :
                                    <div
                                        style={{
                                            display: 'block',
                                            height: canvasHeight,
                                            width: '100%',
                                        }}
                                    />
                            }
                        </div>
                }
            </div>
        );
    }
}

export default SketchItem;
